import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { QuoteModal } from "@/components/QuoteModal";
import { Card, CardContent } from "@/components/ui/card";
import { useState } from "react";

const CookiePolicy = () => {
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);

  const cookieTypes = [
    {
      type: "Essential Cookies",
      purpose: "These cookies are necessary for the website to function properly. They enable core functionality such as security, network management, and accessibility.",
      examples: "Session ID, security tokens, user preferences",
      duration: "Session or up to 1 year"
    },
    {
      type: "Analytics Cookies",
      purpose: "These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.",
      examples: "Google Analytics, page views, user journeys",
      duration: "Up to 2 years"
    },
    {
      type: "Functional Cookies",
      purpose: "These cookies enable enhanced functionality and personalization, such as remembering your preferences and settings.",
      examples: "Language preference, region selection, tracking preferences",
      duration: "Up to 1 year"
    },
    {
      type: "Marketing Cookies",
      purpose: "These cookies are used to track visitors across websites to display relevant advertisements.",
      examples: "Retargeting ads, social media sharing",
      duration: "Up to 2 years"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar onQuoteClick={() => setQuoteModalOpen(true)} />

      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-8 animate-fade-in">Cookie Policy</h1>
          <p className="text-muted-foreground mb-8">Last updated: January 2025</p>

          <div className="prose prose-lg max-w-none space-y-8">
            <section className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-4">What Are Cookies?</h2>
              <p className="text-muted-foreground mb-4">
                Cookies are small text files that are placed on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently and provide information to website owners.
              </p>
              <p className="text-muted-foreground">
                At Costal Meridian, we use cookies to enhance your browsing experience, analyze site traffic, personalize content, and serve targeted advertisements. This policy explains what cookies we use, why we use them, and how you can control them.
              </p>
            </section>

            <section className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-6">Types of Cookies We Use</h2>
              <div className="grid gap-6">
                {cookieTypes.map((cookie, index) => (
                  <Card key={index}>
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold mb-3">{cookie.type}</h3>
                      <p className="text-muted-foreground mb-4">{cookie.purpose}</p>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-semibold">Examples:</span>
                          <p className="text-muted-foreground">{cookie.examples}</p>
                        </div>
                        <div>
                          <span className="font-semibold">Duration:</span>
                          <p className="text-muted-foreground">{cookie.duration}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            <section className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-4">Third-Party Cookies</h2>
              <p className="text-muted-foreground mb-4">
                In addition to our own cookies, we may also use various third-party cookies to report usage statistics, deliver advertisements, and so forth. These include:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Google Analytics:</strong> Helps us understand how visitors use our site</li>
                <li><strong>Google Ads:</strong> Delivers relevant advertisements based on your interests</li>
                <li><strong>Facebook Pixel:</strong> Enables social media integration and advertising</li>
                <li><strong>LinkedIn Insight Tag:</strong> Helps us reach professional audiences</li>
                <li><strong>Hotjar:</strong> Provides insights into user behavior through heatmaps</li>
              </ul>
            </section>

            <section className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-4">How to Control Cookies</h2>
              <p className="text-muted-foreground mb-4">
                You have the right to decide whether to accept or reject cookies. You can exercise your cookie rights by setting your preferences in our Cookie Consent Manager, which appears when you first visit our site.
              </p>
              <h3 className="text-xl font-semibold mb-3">Browser Controls</h3>
              <p className="text-muted-foreground mb-4">
                Most web browsers allow you to control cookies through their settings. You can usually find these settings in the "Options" or "Preferences" menu of your browser. The following links may be helpful:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Chrome: Settings → Privacy and security → Cookies</li>
                <li>Firefox: Settings → Privacy & Security → Cookies</li>
                <li>Safari: Preferences → Privacy → Cookies</li>
                <li>Edge: Settings → Privacy, search, and services → Cookies</li>
              </ul>
            </section>

            <section className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-4">Impact of Disabling Cookies</h2>
              <p className="text-muted-foreground mb-4">
                Please note that if you choose to disable cookies, some parts of our website may not work properly. For example:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>You may not be able to stay logged in to your account</li>
                <li>Your preferences and settings may not be saved</li>
                <li>Some features of our tracking portal may be limited</li>
                <li>The website may load more slowly</li>
              </ul>
            </section>

            <section className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-4">Cookie Consent</h2>
              <p className="text-muted-foreground mb-4">
                When you first visit our website, you will be presented with a cookie consent banner. You can choose to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Accept All:</strong> Enable all cookies including analytics and marketing</li>
                <li><strong>Essential Only:</strong> Only enable cookies necessary for the website to function</li>
                <li><strong>Customize:</strong> Choose which types of cookies to enable</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                You can change your preferences at any time by clicking on the "Cookie Settings" link in our website footer.
              </p>
            </section>

            <section className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-4">Updates to This Policy</h2>
              <p className="text-muted-foreground">
                We may update this Cookie Policy from time to time to reflect changes in technology, legislation, or our data practices. When we make changes, we will update the "Last updated" date at the top of this policy. We encourage you to review this policy periodically.
              </p>
            </section>

            <section className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-4">Contact Us</h2>
              <p className="text-muted-foreground mb-4">
                If you have any questions about our use of cookies, please contact us:
              </p>
              <div className="bg-secondary p-6 rounded-lg">
                <p className="text-muted-foreground">
                  <strong>Costal Meridian Cookie Inquiries</strong><br />
                  555 Anton Blvd suite 1050<br />
                  Costa Mesa, CA 92626, USA<br />
                  Email: support@costalmeridian.com<br />
                  Phone: (714) 798-3650
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
      <QuoteModal open={quoteModalOpen} onOpenChange={setQuoteModalOpen} />
    </div>
  );
};

export default CookiePolicy;
